import { type FunctionComponent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";

import { asBadgeCategory, type ClientBadgeContext } from "@fitness-app/data-models/entities/Badge";

import { badgeCategoryColors, badgeCategoryLabels } from "~/modules/Badges/constants";
import { supabase } from "~/store/initializeStore";
import { challengesHabitsKeys } from "./queryKeys";

type TraineeBadgeRow = {
  id: string;
  badgeKey: string;
  tier: number;
  earnedAt: string;
  context: ClientBadgeContext | null;
  badge: {
    name: string;
    icon: string;
    color: string;
    category: string;
  } | null;
};

type Props = {
  traineeId: string;
};

const formatContextHint = (context: ClientBadgeContext | null): string => {
  if (context?.challengeId) {
    return "wyzwanie";
  }

  if (context?.clientHabitId) {
    return "nawyk / zadanie";
  }

  if (context?.goalId && typeof context.threshold === "number") {
    const tierLabels: Record<string, string> = { bronze: "brąz", silver: "srebro", gold: "złoto" };
    const tierLabel = context.tierName ? (tierLabels[context.tierName] ?? context.tierName) : null;
    return `cel wagowy${tierLabel ? ` (${tierLabel})` : ""} — próg ${context.threshold} kg`;
  }

  return "-";
};

const TraineeBadgesSection: FunctionComponent<Props> = ({ traineeId }) => {
  const badgesQuery = useQuery({
    queryKey: challengesHabitsKeys.badges(traineeId),
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_badge")
        .select("id, badgeKey, tier, earnedAt, context, badge:badgeId(name, icon, color, category)")
        .eq("traineeId", traineeId)
        .order("earnedAt", { ascending: false })
        .returns<TraineeBadgeRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  return (
    <Card title="Odznaki">
      <Table<TraineeBadgeRow>
        dataSource={badgesQuery.data ?? []}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={badgesQuery.isFetching}
        locale={{ emptyText: "Brak zdobytych odznak" }}
        pagination={false}
      >
        <Table.Column<TraineeBadgeRow>
          title="Odznaka"
          key="badge"
          render={(_, row) => (
            <Space size={8}>
              <span style={{ fontSize: 24 }}>{row.badge?.icon ?? "🏅"}</span>
              <Typography.Text strong>{row.badge?.name ?? row.badgeKey}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<TraineeBadgeRow>
          title="Kategoria"
          key="category"
          render={(_, row) => {
            if (!row.badge?.category) {
              return "-";
            }

            const badgeCategory = asBadgeCategory(row.badge.category);

            return (
              <Tag color={row.badge.color || badgeCategoryColors[badgeCategory] || "default"}>
                {badgeCategoryLabels[badgeCategory]}
              </Tag>
            );
          }}
        />
        <Table.Column<TraineeBadgeRow>
          title="Tier"
          key="tier"
          width={130}
          render={(_, row) => {
            if (typeof row.context?.streak === "number") {
              return <Tag color="volcano">{`seria ${row.context.streak} dni`}</Tag>;
            }

            if (row.tier > 1) {
              return <Tag>{`Tier ${row.tier}`}</Tag>;
            }

            return "-";
          }}
        />
        <Table.Column<TraineeBadgeRow>
          title="Kontekst"
          key="context"
          render={(_, row) => <Typography.Text type="secondary">{formatContextHint(row.context)}</Typography.Text>}
        />
        <Table.Column<TraineeBadgeRow>
          title="Zdobyta"
          dataIndex="earnedAt"
          key="earnedAt"
          width={120}
          render={(earnedAt: string) => (earnedAt ? dayjs(earnedAt).format("DD.MM.YYYY") : "-")}
        />
      </Table>
    </Card>
  );
};

export default TraineeBadgesSection;
